import { Box, Collapse } from "@chakra-ui/react";
import { isEqual } from "lodash";
import { ReactNode, createContext, useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import { useCompareEffect, useRect } from "@nens/react/hooks";

import { BackButton } from "@/components/back-button";
import { useRanaSearchState } from "@/hooks/router/rana-search-state";

// FUTURE there is probably a cleaner way to do this, but for now this works.
// The idea is that a child component can set whether it has a back button or not,
// and the parent will then show the back button if the child has one.
// This is needed because the parent doesn't know what the child is doing,
// and the child doesn't know what the parent is doing. So we need to communicate between them.
type CatalogueSectionChildState = {
  hasBackButton: boolean; // Child itself has back button so hide the parent back button
  hasStickyElement: boolean; // Child has a sticky element, so the parent should omit all the collapses and just show the child directly (only use this on detail pages when "hasBackButton" is true, not on overviews because there are no transitions in this state)
  hasExpandedState: boolean; // Child has an expanded state, so opening the section should be supported
};

const ChildStateContext = createContext<
  (
    state:
      | CatalogueSectionChildState
      | ((prev: CatalogueSectionChildState) => CatalogueSectionChildState)
  ) => void
>(() => {
  // noop
});

export const useSetCatalogueSectionChildState = (newState: CatalogueSectionChildState) => {
  const setChildState = useContext(ChildStateContext);
  useEffect(() => {
    setChildState((prev) => (isEqual(prev, newState) ? prev : newState));
  }, [newState, setChildState]);
};

const OPEN_ID_SEARCH_PARAM_KEY = "openId";

export const CatalogueSection = ({
  id,
  openText,
  children,
}: {
  id: string;
  openText: string;
  children: (isOpen: boolean) => ReactNode;
}) => {
  const [openId, setOpenId] = useRanaSearchState<string | null>(OPEN_ID_SEARCH_PARAM_KEY, null, [
    "string",
    "null",
  ]);

  const isOpen = openId === id;
  const noneSelected = openId === null;

  // We want to remember the height of a section when none are selected so we can animate to that when a section is closed again.
  const ref = useRef<HTMLDivElement>(null);
  const { height } = useRect(ref);

  const [childState, setChildState] = useState<CatalogueSectionChildState>({
    hasBackButton: false,
    hasStickyElement: false,
    hasExpandedState: true,
  });
  const { hasBackButton, hasStickyElement, hasExpandedState } = childState;
  useRiskyStateWarnings(childState, isOpen);

  // If the child has a sticky element, we want to omit the collapses and just show the child directly.
  const omitCollapses = hasStickyElement && hasBackButton && isOpen; // All the other checks then `hasStickyElement` are just to prevent the states described in `useRiskyStateWarnings`.

  const heightWhenNoneSelected = useRef(0);

  if (noneSelected && !hasBackButton && height > 0) {
    heightWhenNoneSelected.current = height;
  }

  return (
    <ChildStateContext.Provider value={setChildState}>
      {omitCollapses || !hasExpandedState ? (
        children(isOpen)
      ) : (
        <>
          <Collapse
            in={isOpen || noneSelected}
            endingHeight={noneSelected ? heightWhenNoneSelected.current || "auto" : undefined}
            unmountOnExit
          >
            <Collapse in={isOpen && !hasBackButton}>
              <BackButton back={() => setOpenId(null, { clearOthers: "all" })}>
                <FormattedMessage id="button.back_to_overview" defaultMessage="Back to overview" />
              </BackButton>
            </Collapse>
            <Box
              // Use onClickCapture instead of onClick so that the click is captured before the child
              // This makes it so if the child has a button that navigates to a detail page and you click the browsers back button after
              // that the detail page is closed instead of the section being closed while the detail page remains open.
              onClickCapture={() => setOpenId(id)}
              overflow="visible"
              position="relative"
            >
              <Box ref={ref} h="fit">
                {children(isOpen)}
              </Box>
            </Box>
          </Collapse>
          <Collapse unmountOnExit in={noneSelected}>
            <BackButton ml="auto" isForward back={() => setOpenId(id)}>
              {openText}
            </BackButton>
          </Collapse>
        </>
      )}
    </ChildStateContext.Provider>
  );
};

// Some warnings for states we don't want to happen. Also be useful for understanding the logic above.
const useRiskyStateWarnings = (childState: CatalogueSectionChildState, isOpen: boolean) => {
  useCompareEffect(() => {
    const { hasBackButton, hasStickyElement } = childState;
    if (!hasBackButton && hasStickyElement) {
      console.warn(
        "CatalogueSection: hasStickyElement is true but hasBackButton is false. This won't work because the section will not omit the collapses if the child doesn't have a back button, otherwise there will be no back button and/or transitions."
      );
    }

    if (hasBackButton && !isOpen) {
      console.warn(
        "CatalogueSection: hasBackButton is true but the section is not open. This should not happen because having a back button means a different component is open than the one on the overview page. Which means the section should be open."
      );
    }
  }, [childState, isOpen]);
};
